import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input,
  Text,
  Textarea,
  useToast,
  UseToastOptions,
  VStack,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useState } from 'react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { useParams } from 'react-router-dom';
import { Info } from 'lucide-react';
import { LocationError } from './LocationError';

interface RegionLocationFormProps {
  id?: string;
  name: string;
  description?: string;
}

const defaultFormState: RegionLocationFormProps = {
  name: '',
  description: '',
};

export function RegionForm({
  region,
  onClose,
}: {
  region?: NonNullable<RouterOutputs['providers']['locations']['getSiteRegion']>;
  onClose?: () => void;
}) {
  const { providerId } = useParams<{ providerId: string }>();
  const [nameToValidate, setNameToValidate] = useState<boolean>(false);

  const {
    handleSubmit,
    register,
    clearErrors,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting, isLoading },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
    defaultValues: region
      ? {
          name: region.name,
          description: region.description,
        }
      : {
          ...defaultFormState,
        },
  });

  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const name = watch('name');

  if (!providerId) {
    return <LocationError message={t`An error occurred, providerId is required.`} />;
  }

  const { refetch: checkNameExistence } = trpcAdmin.providers.locations.searchRegion.useQuery(
    {
      providerId,
      name,
    },
    {
      enabled: !!nameToValidate,
    },
  );

  const toastMessage: UseToastOptions = {
    title: region
      ? t`Region location updated successfully.`
      : t`Region location created successfully.`,
    status: 'success',
    duration: 7000,
    isClosable: true,
  };

  const createSiteRegion = trpcAdmin.providers.locations.createSiteRegion.useMutation({
    onSuccess: () => {
      toast(toastMessage);
      onClose?.();
      reset();
      void utils.providers.locations.listSiteRegionsWithPagination.refetch();
    },
  });

  const updateSiteRegion = trpcAdmin.providers.locations.updateSiteRegion.useMutation({
    onSuccess: () => {
      toast(toastMessage);
      void utils.providers.locations.getSiteRegion.refetch();
      void utils.providers.locations.listSiteRegionsWithPagination.refetch();
    },
  });

  const validateName = async () => {
    if (!name) {
      return true;
    }

    // Skip validation if region prop is passed and name is unchanged
    if (region && name === region.name) {
      return true;
    }

    setNameToValidate(true);
    const result = await checkNameExistence();
    return !result.data;
  };

  const onSubmit = async (data) => {
    const isNameValid = await validateName();
    if (!isNameValid) {
      setError('name', {
        type: 'manual',
        message: 'Region already exists. Please choose a unique name.',
      });

      setNameToValidate(false);
      return;
    }

    if (region?.id) {
      await updateSiteRegion.mutateAsync({
        providerId,
        id: region.id,
        data,
      });
    } else {
      await createSiteRegion.mutateAsync({
        providerId,
        data,
      });
    }
  };

  const isDefaultStateRegionName = region ? region.is_default : false;

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }} noValidate>
      <VStack spacing={4} align="stretch" p={4}>
        <FormControl isInvalid={!!errors.name} isRequired>
          <FormLabel htmlFor="RegionForm-name">
            <Trans>Name</Trans>
          </FormLabel>
          <Input
            id="RegionForm-name"
            autoComplete="off"
            {...register('name', {
              required: t`Name is required`,
              onChange: () => {
                if (errors.name) {
                  clearErrors('name');
                }
              },
            })}
            isDisabled={isDefaultStateRegionName}
          />
          <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          {isDefaultStateRegionName ? (
            <Box fontWeight="medium" color={'warning'} mt={2}>
              <HStack spacing={1}>
                <Icon as={Info} boxSize="4" />
                <Text fontSize="sm">
                  <Trans>Updating the primary region name is not allowed.</Trans>
                </Text>
              </HStack>
            </Box>
          ) : null}
        </FormControl>

        <FormControl>
          <FormLabel htmlFor="RegionForm-description">
            <Trans>Description</Trans>
          </FormLabel>
          <Textarea
            id="RegionForm-description"
            placeholder="Add a description"
            size="sm"
            {...register('description')}
          />
        </FormControl>

        <Flex justify={'flex-end'}>
          <Button
            right={0}
            type="submit"
            isLoading={isSubmitting}
            isDisabled={
              region
                ? updateSiteRegion.isPending || isSubmitting || isLoading
                : createSiteRegion.isPending || isSubmitting || isLoading
            }
          >
            {region ? t`Update region` : t`Add region`}
          </Button>
        </Flex>
      </VStack>
    </form>
  );
}
